import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useState,
  useCallback,
} from "react";
import useFetchDomains from "../../../hooks/useFetchDomains";
import FlexBetween from "../../FlexBetween";
import { Autocomplete, TextField } from "@mui/material";

interface IAutoCompleteDomainList {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  handleOnChangeDomain: (event: ChangeEvent<any>, value: any) => void;
  currentDomainId: string | number | null;
  className?: string;
  name?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  lazyLoad?: boolean; // ✅ NEW: Controls whether to fetch data on mount or on dropdown open
}

/**
 * All domains list with auto complete dropdown functionality
 * @returns
 * Selected domain with id and name
 */
const AutoCompleteDomainListFullWidth: React.FC<IAutoCompleteDomainList> = ({
  setIsLoading,
  handleOnChangeDomain,
  currentDomainId,
  className,
  name,
  error,
  helperText,
  required,
  disabled,
  lazyLoad = false, // ✅ NEW: Default to false for backward compatibility
}) => {
  // Track if dropdown has been opened (for lazy loading)
  const [hasOpened, setHasOpened] = useState(false);

  const [domainsData, fetchDomains, isFetching] = useFetchDomains({
    setIsLoading,
    fetchOnMount: !lazyLoad,
  });

  // Handle dropdown open - trigger fetch if lazy loading
  const handleOpen = useCallback(() => {
    if (lazyLoad && !hasOpened) {
      setHasOpened(true);
      fetchDomains();
    }
  }, [lazyLoad, hasOpened, fetchDomains]);

  // Show loading spinner while fetching data
  const isLoading = lazyLoad && (isFetching || !hasOpened);

  return (
    <FlexBetween>
      <Autocomplete
        className={className}
        defaultValue={currentDomainId || ""}
        options={domainsData || []}
        getOptionLabel={(option) => option.domain_name || ""}
        value={
          (domainsData || []).find((option) => option.id === currentDomainId) ||
          null
        }
        disabled={disabled}
        onOpen={handleOpen}
        loading={isLoading}
        renderInput={(params) => (
          <TextField
            name={name}
            style={{ color: "#000000" }}
            {...params}
            label={
              <span>
                Domain
                {required && <span className="required-asterisk">*</span>}
              </span>
            }
            placeholder="Select..."
            InputProps={{
              ...params.InputProps,
              autoComplete: "new-password", // Fix for browser autofill issue
            }}
            InputLabelProps={{
              shrink: true,
            }}
            error={error}
            helperText={helperText}
          />
        )}
        fullWidth
        loadingText="Loading..."
        onChange={(event, value) => handleOnChangeDomain(event, value)}
      />
    </FlexBetween>
  );
};

export default AutoCompleteDomainListFullWidth;
